import React, { Component, MouseEvent } from "react";
import classnames from "classnames";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { ILabeledPolygon } from "@formant/core/model/ILabeledPolygon";
import { Measure } from "@formant/ui/component/common/Measure";

import { Escape } from "../../common/Escape";
import { ScaledImageCoordinates } from "../../common/ScaledImageCoordinates";
import { AssetImage } from "../AssetImage";
import * as styles from "./LabeledPolygonInput.scss";

export interface ILabeledPolygonInputProps {
    imageUrl: string;
    value: ILabeledPolygon[];
    activeIndex?: number;
    readOnly: boolean;
    onChange: (value: ILabeledPolygon[]) => void;
    onActivate: (index?: number) => void;
}

interface IVertexProps {
    vertex: [number, number];
}

const Vertex = ({ vertex: [x, y] }: IVertexProps) => {
    return <circle cx={x} cy={y} r={2} className={styles.vertex} />;
};

@observer
export class LabeledPolygonInput extends Component<ILabeledPolygonInputProps> {
    private scaledImageCoordinates: ScaledImageCoordinates =
        new ScaledImageCoordinates(1, 1, 1, 1);

    @observable
    private active: [number, number][] = [];
    @observable
    private activeBox?: [[number, number], [number, number]];

    private get activeBoxVertices(): [number, number][] | undefined {
        return (
            (this.activeBox && [
                this.activeBox[0],
                [this.activeBox[1][0], this.activeBox[0][1]],
                this.activeBox[1],
                [this.activeBox[0][0], this.activeBox[1][1]],
            ]) ||
            undefined
        );
    }

    public render() {
        const { scaledImageCoordinates } = this;
        const { value, activeIndex, imageUrl, onActivate } = this.props;

        return (
            <Measure
                className={styles["labeled-polygon-input"]}
                onMeasureResize={scaledImageCoordinates.onResize}
                onMouseDown={this.onMouseDown}
                onMouseUp={this.onMouseUp}
                onMouseMove={this.onMouseMove}
            >
                <Escape onEsc={this.onEsc} />
                <AssetImage
                    src={imageUrl}
                    onImageSize={scaledImageCoordinates.onImageSize}
                />
                <svg>
                    {value.map(({ vertices }, i) => {
                        const onMouseOver = () => onActivate(i);
                        const onMouseOut = () => onActivate(undefined);

                        return (
                            <g key={i}>
                                <polygon
                                    className={classnames(
                                        styles.polygon,
                                        activeIndex === i && styles.active,
                                    )}
                                    points={vertices
                                        .map(([x, y]) =>
                                            scaledImageCoordinates.project(
                                                x,
                                                y,
                                            ),
                                        )
                                        .map(([x, y]) => `${x},${y}`)
                                        .join(" ")}
                                    onMouseOver={onMouseOver}
                                    onMouseOut={onMouseOut}
                                />
                                {vertices.map((vertex, j) => (
                                    <Vertex
                                        key={j}
                                        vertex={scaledImageCoordinates.project(
                                            ...vertex,
                                        )}
                                    />
                                ))}
                            </g>
                        );
                    })}
                    <path
                        className={styles.polygon}
                        d={this.active
                            .map(([x, y]) =>
                                scaledImageCoordinates.project(x, y),
                            )
                            .reduce(
                                (acc, [x, y], i) =>
                                    i === 0
                                        ? `M ${x} ${y}`
                                        : `${acc} L ${x} ${y}`,
                                "",
                            )}
                    />
                    {this.active.map((vertex, i) => (
                        <Vertex
                            key={i}
                            vertex={scaledImageCoordinates.project(...vertex)}
                        />
                    ))}
                    {this.activeBoxVertices && (
                        <g>
                            <polygon
                                className={styles.polygon}
                                points={this.activeBoxVertices
                                    .map(([x, y]) =>
                                        scaledImageCoordinates.project(x, y),
                                    )
                                    .map(([x, y]) => `${x},${y}`)
                                    .join(" ")}
                            />
                            {this.activeBoxVertices.map((vertex, j) => (
                                <Vertex
                                    key={j}
                                    vertex={scaledImageCoordinates.project(
                                        ...vertex,
                                    )}
                                />
                            ))}
                        </g>
                    )}
                </svg>
            </Measure>
        );
    }

    private add() {
        if (this.active.length > 0) {
            this.props.onChange([
                ...this.props.value,
                { vertices: this.active, labels: [] },
            ]);
        }
        this.active = [];
    }

    private addBox() {
        if (this.activeBoxVertices) {
            this.active = this.activeBoxVertices;
            this.add();
            this.activeBox = undefined;
        }
    }

    private onMouseDown = (event: MouseEvent<HTMLDivElement>) => {
        if (this.props.readOnly) {
            return;
        }

        event.preventDefault();
        const target = event.currentTarget;
        const { left, top } = target.getBoundingClientRect();
        const [x, y] = this.scaledImageCoordinates.unproject(
            event.pageX - left,
            event.pageY - top,
        );

        if (event.shiftKey) {
            this.activeBox = [
                [x, y],
                [x, y],
            ];
            return;
        }

        if (this.active.length === 0) {
            this.active = [[x, y]];
            return;
        }

        if (this.active.length > 2) {
            const [x0, y0] = this.scaledImageCoordinates.project(
                ...this.active[0],
            );
            const [x1, y1] = this.scaledImageCoordinates.project(x, y);
            const distance = Math.sqrt(
                (x1 - x0) * (x1 - x0) + (y1 - y0) * (y1 - y0),
            );
            if (distance < 10) {
                this.active = this.active.slice(0, -1);
                this.add();
                return;
            }
        }

        this.active = [...this.active, [x, y]];
    };

    private onMouseUp = () => {
        if (this.activeBox) {
            this.addBox();
        }
    };

    private onMouseMove = (event: MouseEvent<HTMLDivElement>) => {
        const target = event.currentTarget;

        const { left, top } = target.getBoundingClientRect();
        const [x, y] = this.scaledImageCoordinates.unproject(
            event.pageX - left,
            event.pageY - top,
        );

        if (this.activeBox) {
            this.activeBox[1] = [x, y];
            if (!event.buttons && this.activeBoxVertices) {
                this.addBox();
            }
            return;
        }

        if (this.active.length === 0) {
            return;
        } else if (this.active.length === 1) {
            this.active = [...this.active, [x, y]];
        }

        this.active = [...this.active.slice(0, -1), [x, y]];
    };

    private onEsc = () => {
        this.activeBox = undefined;
        this.active = [];
    };
}
